import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, Plus, Trash2, Sparkles, Save, Eye } from 'lucide-react';
import { toast } from 'sonner';

const LOGRO_LEVELS = ['Inicial', 'Intermedio', 'Avanzado'];

const CrearActividad = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [niveles, setNiveles] = useState([]);
  const [especialidades, setEspecialidades] = useState([]);
  const [ras, setRas] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [formData, setFormData] = useState({
    titulo: '',
    descripcion: '',
    instrucciones: '',
    tipo_actividad: 'seleccion_multiple',
    nivel_id: '',
    especialidad_id: '',
    ra_id: '',
    permite_nota: true,
    permite_logro: true,
    indicador_evaluacion: '',
    rubrica_logro: {
      Inicial: '',
      Intermedio: '',
      Avanzado: ''
    },
    permite_intentos: true,
    max_intentos: 3,
    mostrar_retroalimentacion_inmediata: true,
    privada: true,
    contenido_json: null,
    contenido_html: ''
  });
  
  const [preguntas, setPreguntas] = useState([]);
  const [espacios, setEspacios] = useState([]);
  const [columnas, setColumnas] = useState({ izquierda: [], derecha: [] });
  const [secuencia, setSecuencia] = useState([]);
  const [laboratorio, setLaboratorio] = useState({ pasos: [], recursos: '' });
  const [archivoHTML, setArchivoHTML] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [nivelesRes, especialidadesRes, rasRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/niveles`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/especialidades`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/resultados-aprendizaje`, { withCredentials: true })
      ]);
      
      setNiveles(nivelesRes.data);
      setEspecialidades(especialidadesRes.data);
      setRas(rasRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPregunta = () => {
    const nuevaPregunta = {
      id: `pregunta_${Date.now()}`,
      texto: '',
      opciones: ['', '', '', ''],
      respuesta_correcta: 0,
      retroalimentacion: ''
    };
    setPreguntas([...preguntas, nuevaPregunta]);
  };

  const handleRemovePregunta = (id) => {
    setPreguntas(preguntas.filter(p => p.id !== id));
  };

  const handlePreguntaChange = (id, field, value) => {
    setPreguntas(preguntas.map(p => 
      p.id === id ? { ...p, [field]: value } : p
    ));
  };

  const handleOpcionChange = (preguntaId, opcionIndex, value) => {
    setPreguntas(preguntas.map(p => {
      if (p.id === preguntaId) {
        const nuevasOpciones = [...p.opciones];
        nuevasOpciones[opcionIndex] = value;
        return { ...p, opciones: nuevasOpciones };
      }
      return p;
    }));
  };

  const handleGenerarConIA = async () => {
    if (!formData.titulo || !formData.nivel_id) {
      toast.error('Por favor completa el título y selecciona un nivel antes de generar con IA');
      return;
    }

    const nivel = niveles.find(n => n.nivel_id === formData.nivel_id);
    const tema = formData.titulo;
    
    toast.info('Generando actividad con IA... esto puede tomar unos segundos');
    
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/actividades/generar-con-ia?tema=${encodeURIComponent(tema)}&nivel_educativo=${encodeURIComponent(nivel?.nombre || 'Secundaria')}&num_preguntas=5`,
        {},
        { withCredentials: true }
      );
      
      // Actualizar el formulario con los datos generados
      setFormData({
        ...formData,
        descripcion: data.descripcion || formData.descripcion,
        instrucciones: data.instrucciones || formData.instrucciones
      });
      
      // Actualizar las preguntas
      setPreguntas(data.preguntas || []);
      
      toast.success('¡Actividad generada exitosamente! Revisa y ajusta según necesites');
    } catch (error) {
      console.error('Error generating with AI:', error);
      toast.error('Error al generar actividad con IA. Inténtalo de nuevo');
    }
  };

  const handleRubricaChange = (nivel, value) => {
    setFormData({
      ...formData,
      rubrica_logro: {
        ...formData.rubrica_logro,
        [nivel]: value
      }
    });
  };

  const handleGenerarRubricaConIA = async () => {
    if (!formData.permite_logro) {
      toast.error('Activa la medición de logro para generar la rúbrica');
      return;
    }

    if (!formData.indicador_evaluacion.trim()) {
      toast.error('Escribe primero el indicador de evaluación definido por el docente');
      return;
    }

    const nivel = niveles.find((item) => item.nivel_id === formData.nivel_id);
    const ra = ras.find((item) => item.ra_id === formData.ra_id);

    toast.info('Generando rúbrica con IA...');

    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/actividades/generar-rubrica-logro`,
        {
          titulo: formData.titulo,
          descripcion: formData.descripcion,
          tipo_actividad: formData.tipo_actividad,
          indicador_evaluacion: formData.indicador_evaluacion,
          nivel_educativo: nivel?.nombre || '',
          resultado_aprendizaje: ra?.descripcion || ''
        },
        { withCredentials: true }
      );

      setFormData({
        ...formData,
        rubrica_logro: {
          Inicial: data.Inicial || '',
          Intermedio: data.Intermedio || '',
          Avanzado: data.Avanzado || ''
        }
      });
      toast.success('Rúbrica sugerida generada. Revísala y ajústala antes de guardar');
    } catch (error) {
      console.error('Error generating rubrica with AI:', error);
      toast.error('Error al generar la rúbrica con IA');
    }
  };

  const handleGuardar = async (enviarRevision = false) => {
    if (!formData.titulo || !formData.nivel_id || !formData.especialidad_id || !formData.ra_id) {
      toast.error('Por favor completa todos los campos obligatorios');
      return;
    }

    if (formData.permite_logro) {
      if (!formData.indicador_evaluacion.trim()) {
        toast.error('Debes definir el indicador de evaluación');
        return;
      }

      const rubricaCompleta = LOGRO_LEVELS.every(
        (nivel) => formData.rubrica_logro[nivel]?.trim()
      );
      if (!rubricaCompleta) {
        toast.error('Completa la rúbrica de logro para Inicial, Intermedio y Avanzado');
        return;
      }
    }

    let contenidoJSON = null;
    let contenidoHTML = formData.contenido_html;

    // Validar y preparar contenido según el tipo
    if (formData.tipo_actividad === 'seleccion_multiple') {
      if (preguntas.length === 0) {
        toast.error('Agrega al menos una pregunta');
        return;
      }
      contenidoJSON = { preguntas };
    } else if (formData.tipo_actividad === 'completar_espacios') {
      if (espacios.length === 0) {
        toast.error('Agrega al menos un texto con espacios');
        return;
      }
      contenidoJSON = { espacios };
    } else if (formData.tipo_actividad === 'relacionar_columnas') {
      if (columnas.izquierda.length === 0 || columnas.derecha.length === 0) {
        toast.error('Agrega elementos en ambas columnas');
        return;
      }
      contenidoJSON = { columnas };
    } else if (formData.tipo_actividad === 'ordenar_secuencia') {
      if (secuencia.length === 0) {
        toast.error('Agrega pasos a la secuencia');
        return;
      }
      contenidoJSON = { secuencia };
    } else if (formData.tipo_actividad === 'laboratorio_guiado') {
      if (laboratorio.pasos.length === 0) {
        toast.error('Agrega pasos al laboratorio');
        return;
      }
      contenidoJSON = { laboratorio };
    } else if (formData.tipo_actividad === 'html_externo') {
      if (!contenidoHTML) {
        toast.error('Debes cargar un archivo HTML o ingresar código');
        return;
      }
    }

    try {
      const actividadData = {
        ...formData,
        contenido_json: contenidoJSON,
        contenido_html: contenidoHTML
      };

      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/actividades`,
        actividadData,
        { withCredentials: true }
      );

      if (enviarRevision) {
        await axios.patch(
          `${process.env.REACT_APP_BACKEND_URL}/api/actividades/${data.actividad_id}/estado`,
          'revision',
          { 
            withCredentials: true,
            headers: { 'Content-Type': 'application/json' }
          }
        );
        toast.success('Actividad enviada a revisión');
      } else {
        toast.success('Actividad guardada como borrador');
      }

      navigate('/docente/actividades');
    } catch (error) {
      console.error('Error saving actividad:', error);
      toast.error('Error al guardar actividad');
    }
  };


  // Handlers para Completar Espacios
  const handleAddEspacio = () => {
    setEspacios([...espacios, { id: `espacio_${Date.now()}`, texto: '', respuestas: [] }]);
  };

  const handleRemoveEspacio = (id) => {
    setEspacios(espacios.filter(e => e.id !== id));
  };

  const handleEspacioChange = (id, field, value) => {
    setEspacios(espacios.map(e => e.id === id ? { ...e, [field]: value } : e));
  };

  // Handlers para Relacionar Columnas
  const handleAddColumna = (lado) => {
    setColumnas({
      ...columnas,
      [lado]: [...columnas[lado], { id: `col_${Date.now()}`, texto: '' }]
    });
  };

  const handleRemoveColumna = (lado, id) => {
    setColumnas({
      ...columnas,
      [lado]: columnas[lado].filter(c => c.id !== id)
    });
  };

  const handleColumnaChange = (lado, id, value) => {
    setColumnas({
      ...columnas,
      [lado]: columnas[lado].map(c => c.id === id ? { ...c, texto: value } : c)
    });
  };

  // Handlers para Ordenar Secuencia
  const handleAddPaso = () => {
    setSecuencia([...secuencia, { id: `paso_${Date.now()}`, texto: '', orden: secuencia.length + 1 }]);
  };

  const handleRemovePaso = (id) => {
    setSecuencia(secuencia.filter(p => p.id !== id));
  };

  const handlePasoChange = (id, value) => {
    setSecuencia(secuencia.map(p => p.id === id ? { ...p, texto: value } : p));
  };

  // Handlers para Laboratorio Guiado
  const handleAddPasoLab = () => {
    setLaboratorio({
      ...laboratorio,
      pasos: [...laboratorio.pasos, { id: `paso_${Date.now()}`, titulo: '', descripcion: '', verificacion: '' }]
    });
  };

  const handleRemovePasoLab = (id) => {
    setLaboratorio({
      ...laboratorio,
      pasos: laboratorio.pasos.filter(p => p.id !== id)
    });
  };

  const handlePasoLabChange = (id, field, value) => {
    setLaboratorio({
      ...laboratorio,
      pasos: laboratorio.pasos.map(p => p.id === id ? { ...p, [field]: value } : p)
    });
  };

  // Handler para cargar archivo HTML
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.html') && !file.name.endsWith('.htm')) {
      toast.error('Solo se permiten archivos HTML');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setFormData({ ...formData, contenido_html: event.target.result });
      setArchivoHTML(file);
      toast.success('Archivo HTML cargado exitosamente');
    };
    reader.readAsText(file);
  };


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/docente/actividades')}
              data-testid="back-button"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Crear Actividad</h1>
              <p className="text-sm text-muted-foreground">Diseña una nueva actividad didáctica</p>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="space-y-6">
          {/* Información Básica */}
          <Card>
            <CardHeader>
              <CardTitle>Información Básica</CardTitle>
              <CardDescription>Define los detalles principales de la actividad</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="titulo">Título de la Actividad *</Label>
                <Input
                  id="titulo"
                  value={formData.titulo}
                  onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                  placeholder="Ej: Fundamentos de Programación"
                  required
                  data-testid="input-titulo"
                />
              </div>

              <div>
                <Label htmlFor="descripcion">Descripción</Label>
                <textarea
                  id="descripcion"
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={formData.descripcion}
                  onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                  placeholder="Breve descripción de la actividad"
                  data-testid="textarea-descripcion"
                />
              </div>

              <div>
                <Label htmlFor="instrucciones">Instrucciones</Label>
                <textarea
                  id="instrucciones"
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={formData.instrucciones}
                  onChange={(e) => setFormData({ ...formData, instrucciones: e.target.value })}
                  placeholder="Instrucciones detalladas para el estudiante"
                  data-testid="textarea-instrucciones"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="tipo">Tipo de Actividad *</Label>
                  <select
                    id="tipo"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={formData.tipo_actividad}
                    onChange={(e) => setFormData({ ...formData, tipo_actividad: e.target.value })}
                    data-testid="select-tipo"
                  >
                    <option value="seleccion_multiple">Selección Múltiple</option>
                    <option value="completar_espacios">Completar Espacios</option>
                    <option value="relacionar_columnas">Relacionar Columnas</option>
                    <option value="ordenar_secuencia">Ordenar Secuencia</option>
                    <option value="laboratorio_guiado">Laboratorio Guiado</option>
                    <option value="html_externo">HTML/JavaScript Externo</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="nivel">Nivel *</Label>
                  <select
                    id="nivel"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={formData.nivel_id}
                    onChange={(e) => setFormData({ ...formData, nivel_id: e.target.value })}
                    required
                    data-testid="select-nivel"
                  >
                    <option value="">Seleccionar nivel</option>
                    {niveles.map(n => (
                      <option key={n.nivel_id} value={n.nivel_id}>{n.nombre}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="especialidad">Especialidad *</Label>
                  <select
                    id="especialidad"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={formData.especialidad_id}
                    onChange={(e) => setFormData({ ...formData, especialidad_id: e.target.value })}
                    required
                    data-testid="select-especialidad"
                  >
                    <option value="">Seleccionar especialidad</option>
                    {especialidades.map(e => (
                      <option key={e.especialidad_id} value={e.especialidad_id}>{e.nombre}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label htmlFor="ra">Resultado de Aprendizaje *</Label>
                  <select
                    id="ra"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={formData.ra_id}
                    onChange={(e) => setFormData({ ...formData, ra_id: e.target.value })}
                    required
                    data-testid="select-ra"
                  >
                    <option value="">Seleccionar RA</option>
                    {ras.map(r => (
                      <option key={r.ra_id} value={r.ra_id}>{r.codigo} - {r.descripcion.substring(0, 50)}...</option>
                    ))}
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Configuración de Evaluación */}
          <Card>
            <CardHeader>
              <CardTitle>Configuración de Evaluación</CardTitle>
              <CardDescription>Define cómo se evaluará esta actividad</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.permite_nota}
                    onChange={(e) => setFormData({ ...formData, permite_nota: e.target.checked })}
                    className="h-4 w-4"
                    data-testid="checkbox-permite-nota"
                  />
                  Permite calificación numérica
                </Label>
                <Label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.permite_logro}
                    onChange={(e) => setFormData({ ...formData, permite_logro: e.target.checked })}
                    className="h-4 w-4"
                    data-testid="checkbox-permite-logro"
                  />
                  Mide nivel de logro
                </Label>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.permite_intentos}
                    onChange={(e) => setFormData({ ...formData, permite_intentos: e.target.checked })}
                    className="h-4 w-4"
                    data-testid="checkbox-permite-intentos"
                  />
                  Limitar intentos
                </Label>
                {formData.permite_intentos && (
                  <div>
                    <Label htmlFor="max-intentos">Máximo de intentos</Label>
                    <Input
                      id="max-intentos"
                      type="number"
                      min="1"
                      max="10"
                      value={formData.max_intentos}
                      onChange={(e) => setFormData({ ...formData, max_intentos: parseInt(e.target.value) })}
                      data-testid="input-max-intentos"
                    />
                  </div>
                )}
              </div>

              {formData.permite_logro && (
                <div className="space-y-4 rounded-lg border border-border p-4">
                  <div className="space-y-2">
                    <Label htmlFor="indicador-evaluacion">Indicador de evaluación *</Label>
                    <textarea
                      id="indicador-evaluacion"
                      className="flex min-h-[90px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={formData.indicador_evaluacion}
                      onChange={(e) => setFormData({ ...formData, indicador_evaluacion: e.target.value })}
                      placeholder="Redacta el indicador que el docente desea evaluar en esta actividad"
                      data-testid="textarea-indicador-evaluacion"
                    />
                    <p className="text-xs text-muted-foreground">
                      Este indicador siempre lo define el docente. La IA solo puede sugerir la rúbrica por nivel.
                    </p>
                  </div>

                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <Label>Rúbrica de logro</Label>
                      <p className="text-xs text-muted-foreground">
                        Define cómo se evidencia el desempeño en Inicial, Intermedio y Avanzado.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleGenerarRubricaConIA}
                      data-testid="btn-generar-rubrica-ia"
                    >
                      <Sparkles className="h-4 w-4 mr-2" />
                      Sugerir rúbrica con IA
                    </Button>
                  </div>

                  <div className="grid gap-4">
                    {LOGRO_LEVELS.map((nivel) => (
                      <div key={nivel} className="space-y-2">
                        <Label htmlFor={`rubrica-${nivel.toLowerCase()}`}>{nivel} *</Label>
                        <textarea
                          id={`rubrica-${nivel.toLowerCase()}`}
                          className="flex min-h-[88px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          value={formData.rubrica_logro[nivel]}
                          onChange={(e) => handleRubricaChange(nivel, e.target.value)}
                          placeholder={`Describe la evidencia esperada para el nivel ${nivel}`}
                          data-testid={`textarea-rubrica-${nivel.toLowerCase()}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.mostrar_retroalimentacion_inmediata}
                  onChange={(e) => setFormData({ ...formData, mostrar_retroalimentacion_inmediata: e.target.checked })}
                  className="h-4 w-4"
                  data-testid="checkbox-retroalimentacion"
                />
                Mostrar retroalimentación inmediata
              </Label>

              <Label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.privada}
                  onChange={(e) => setFormData({ ...formData, privada: e.target.checked })}
                  className="h-4 w-4"
                  data-testid="checkbox-privada"
                />
                Mantener como actividad privada (no compartir en biblioteca institucional)
              </Label>
            </CardContent>
          </Card>

          {/* Contenido según tipo de actividad */}
          {formData.tipo_actividad === 'seleccion_multiple' && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Preguntas de Selección Múltiple</CardTitle>
                    <CardDescription>Agrega preguntas con opciones de respuesta</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={handleGenerarConIA}
                      data-testid="btn-generar-ia"
                    >
                      <Sparkles className="h-4 w-4 mr-2" />
                      Generar con IA
                    </Button>
                    <Button
                      onClick={handleAddPregunta}
                      data-testid="btn-agregar-pregunta"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Agregar Pregunta
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {preguntas.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No hay preguntas aún. Agrega tu primera pregunta o genera con IA.</p>
                ) : (
                  preguntas.map((pregunta, index) => (
                    <Card key={pregunta.id} className="border-2">
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <CardTitle className="text-lg">Pregunta {index + 1}</CardTitle>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemovePregunta(pregunta.id)}
                            data-testid={`btn-eliminar-pregunta-${index}`}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <Label>Texto de la pregunta</Label>
                          <Input
                            value={pregunta.texto}
                            onChange={(e) => handlePreguntaChange(pregunta.id, 'texto', e.target.value)}
                            placeholder="Escribe la pregunta aquí"
                            data-testid={`input-pregunta-texto-${index}`}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Opciones de respuesta</Label>
                          {pregunta.opciones.map((opcion, opcionIndex) => (
                            <div key={opcionIndex} className="flex items-center gap-2">
                              <input
                                type="radio"
                                name={`respuesta-${pregunta.id}`}
                                checked={pregunta.respuesta_correcta === opcionIndex}
                                onChange={() => handlePreguntaChange(pregunta.id, 'respuesta_correcta', opcionIndex)}
                                className="h-4 w-4"
                                data-testid={`radio-opcion-${index}-${opcionIndex}`}
                              />
                              <Input
                                value={opcion}
                                onChange={(e) => handleOpcionChange(pregunta.id, opcionIndex, e.target.value)}
                                placeholder={`Opción ${opcionIndex + 1}`}
                                data-testid={`input-opcion-${index}-${opcionIndex}`}
                              />
                            </div>
                          ))}
                          <p className="text-xs text-muted-foreground">Marca el radio button de la respuesta correcta</p>
                        </div>

                        <div>
                          <Label>Retroalimentación (opcional)</Label>
                          <Input
                            value={pregunta.retroalimentacion}
                            onChange={(e) => handlePreguntaChange(pregunta.id, 'retroalimentacion', e.target.value)}
                            placeholder="Mensaje para el estudiante"
                            data-testid={`input-retroalimentacion-${index}`}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </CardContent>
            </Card>
          )}

          {formData.tipo_actividad === 'completar_espacios' && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Completar Espacios</CardTitle>
                    <CardDescription>Crea textos con espacios en blanco para completar</CardDescription>
                  </div>
                  <Button onClick={handleAddEspacio} data-testid="btn-agregar-espacio">
                    <Plus className="h-4 w-4 mr-2" />
                    Agregar Texto
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {espacios.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No hay textos aún. Agrega el primer texto.</p>
                ) : (
                  espacios.map((espacio, index) => (
                    <Card key={espacio.id} className="border-2">
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <CardTitle className="text-lg">Texto {index + 1}</CardTitle>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveEspacio(espacio.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <Label>Texto (usa [___] para espacios en blanco)</Label>
                          <textarea
                            className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
                            value={espacio.texto}
                            onChange={(e) => handleEspacioChange(espacio.id, 'texto', e.target.value)}
                            placeholder="Ejemplo: La capital de Francia es [___] y su idioma principal es el [___]."
                          />
                          <p className="text-xs text-muted-foreground mt-2">
                            Espacios detectados: {(espacio.texto.match(/\[___\]/g) || []).length}
                          </p>
                        </div>
                        <div>
                          <Label>Respuestas correctas (una por línea, en orden)</Label>
                          <textarea
                            className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            value={Array.isArray(espacio.respuestas) ? espacio.respuestas.join('\n') : ''}
                            onChange={(e) => handleEspacioChange(espacio.id, 'respuestas', e.target.value.split('\n'))}
                            placeholder="París\nfrancés"
                          />
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </CardContent>
            </Card>
          )}

          {formData.tipo_actividad === 'relacionar_columnas' && (
            <Card>
              <CardHeader>
                <CardTitle>Relacionar Columnas</CardTitle>
                <CardDescription>Crea pares de elementos para relacionar</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <Label>Columna Izquierda</Label>
                      <Button size="sm" onClick={() => handleAddColumna('izquierda')}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {columnas.izquierda.map((item, index) => (
                        <div key={item.id} className="flex items-center gap-2">
                          <span className="text-sm font-medium w-6">{index + 1}.</span>
                          <Input
                            value={item.texto}
                            onChange={(e) => handleColumnaChange('izquierda', item.id, e.target.value)}
                            placeholder="Elemento"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveColumna('izquierda', item.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <Label>Columna Derecha (en orden de coincidencia)</Label>
                      <Button size="sm" onClick={() => handleAddColumna('derecha')}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {columnas.derecha.map((item, index) => (
                        <div key={item.id} className="flex items-center gap-2">
                          <span className="text-sm font-medium w-6">{index + 1}.</span>
                          <Input
                            value={item.texto}
                            onChange={(e) => handleColumnaChange('derecha', item.id, e.target.value)}
                            placeholder="Coincidencia"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveColumna('derecha', item.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-4">
                  Nota: El elemento 1 de la izquierda coincide con el elemento 1 de la derecha, etc.
                </p>
              </CardContent>
            </Card>
          )}

          {formData.tipo_actividad === 'ordenar_secuencia' && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Ordenar Secuencia</CardTitle>
                    <CardDescription>Crea pasos que el estudiante debe ordenar correctamente</CardDescription>
                  </div>
                  <Button onClick={handleAddPaso} data-testid="btn-agregar-paso">
                    <Plus className="h-4 w-4 mr-2" />
                    Agregar Paso
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {secuencia.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No hay pasos aún. Agrega el primer paso.</p>
                  ) : (
                    secuencia.map((paso, index) => (
                      <div key={paso.id} className="flex items-center gap-2 p-3 border-2 border-border rounded-lg">
                        <span className="text-lg font-bold w-8">{index + 1}</span>
                        <Input
                          value={paso.texto}
                          onChange={(e) => handlePasoChange(paso.id, e.target.value)}
                          placeholder="Describe este paso"
                          className="flex-1"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemovePaso(paso.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-4">
                  Los estudiantes verán estos pasos en orden aleatorio y deberán ordenarlos correctamente.
                </p>
              </CardContent>
            </Card>
          )}

          {formData.tipo_actividad === 'laboratorio_guiado' && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Laboratorio Guiado</CardTitle>
                    <CardDescription>Crea un laboratorio paso a paso con verificaciones</CardDescription>
                  </div>
                  <Button onClick={handleAddPasoLab} data-testid="btn-agregar-paso-lab">
                    <Plus className="h-4 w-4 mr-2" />
                    Agregar Paso
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label>Recursos necesarios</Label>
                  <textarea
                    className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={laboratorio.recursos}
                    onChange={(e) => setLaboratorio({ ...laboratorio, recursos: e.target.value })}
                    placeholder="Lista de materiales, software, o recursos necesarios"
                  />
                </div>

                {laboratorio.pasos.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No hay pasos aún. Agrega el primer paso del laboratorio.</p>
                ) : (
                  laboratorio.pasos.map((paso, index) => (
                    <Card key={paso.id} className="border-2">
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <CardTitle className="text-lg">Paso {index + 1}</CardTitle>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemovePasoLab(paso.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <Label>Título del paso</Label>
                          <Input
                            value={paso.titulo}
                            onChange={(e) => handlePasoLabChange(paso.id, 'titulo', e.target.value)}
                            placeholder="Ej: Configurar el entorno"
                          />
                        </div>
                        <div>
                          <Label>Descripción detallada</Label>
                          <textarea
                            className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            value={paso.descripcion}
                            onChange={(e) => handlePasoLabChange(paso.id, 'descripcion', e.target.value)}
                            placeholder="Instrucciones detalladas para completar este paso"
                          />
                        </div>
                        <div>
                          <Label>Criterio de verificación</Label>
                          <Input
                            value={paso.verificacion}
                            onChange={(e) => handlePasoLabChange(paso.id, 'verificacion', e.target.value)}
                            placeholder="¿Cómo saber que se completó correctamente?"
                          />
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </CardContent>
            </Card>
          )}

          {formData.tipo_actividad === 'html_externo' && (
            <Card>
              <CardHeader>
                <CardTitle>Actividad HTML/JavaScript</CardTitle>
                <CardDescription>Carga un archivo HTML o escribe código personalizado</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label>Cargar archivo HTML</Label>
                  <Input
                    type="file"
                    accept=".html,.htm"
                    onChange={handleFileUpload}
                    className="cursor-pointer"
                  />
                  {archivoHTML && (
                    <p className="text-sm text-green-600 mt-2">✓ {archivoHTML.name} cargado</p>
                  )}
                </div>

                <div className="relative">
                  <div className="flex items-center justify-between mb-2">
                    <Label>O escribe tu código HTML/JavaScript</Label>
                    <span className="text-xs text-muted-foreground">
                      {formData.contenido_html.length} caracteres
                    </span>
                  </div>
                  <textarea
                    className="flex min-h-[300px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
                    value={formData.contenido_html}
                    onChange={(e) => setFormData({ ...formData, contenido_html: e.target.value })}
                    placeholder={`<!DOCTYPE html>
<html>
<head>
  <title>Mi Actividad</title>
  <style>
    /* Tu CSS aquí */
  </style>
</head>
<body>
  <h1>Actividad Interactiva</h1>
  <!-- Tu contenido HTML aquí -->
  
  <script>
    // Tu JavaScript aquí
  </script>
</body>
</html>`}
                  />
                </div>

                {formData.contenido_html && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>Vista Previa</Label>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const blob = new Blob([formData.contenido_html], { type: 'text/html' });
                          const url = URL.createObjectURL(blob);
                          window.open(url, '_blank');
                        }}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        Abrir en nueva ventana
                      </Button>
                    </div>
                    <div className="border-2 border-border rounded-lg overflow-hidden">
                      <iframe
                        srcDoc={formData.contenido_html}
                        className="w-full h-[400px] bg-white"
                        sandbox="allow-scripts"
                        title="Preview"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      ⚠️ El HTML se ejecutará en un entorno aislado (sandbox) por seguridad
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Botones de Acción */}
          <div className="flex gap-2 justify-end sticky bottom-4 bg-background/80 backdrop-blur-xl p-4 rounded-lg border border-border">
            <Button
              variant="outline"
              onClick={() => navigate('/docente/actividades')}
              data-testid="btn-cancelar"
            >
              Cancelar
            </Button>
            <Button
              variant="outline"
              onClick={() => handleGuardar(false)}
              data-testid="btn-guardar-borrador"
            >
              <Save className="h-4 w-4 mr-2" />
              Guardar Borrador
            </Button>
            <Button
              onClick={() => handleGuardar(!formData.privada)}
              data-testid="btn-guardar-y-enviar"
            >
              {formData.privada ? 'Guardar como Privada' : 'Enviar a Revisión'}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CrearActividad;
